import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  siError,
  useSaveWeights,
  useWeights,
} from '../../hooks/useStudentRankings';
import {
  CARD,
  INPUT,
  componentKeys,
  componentLabel,
  formatDay,
} from './rankingText';

const EXPLAINER =
  'Each learner’s score is a weighted average of the components below, each 0–100. Untaken quizzes and unsubmitted or ungraded assignments count as 0, so the score rewards covering the work as well as doing it well. Engagement is days logged in over the last 30. A component with nothing to measure is left out and the others re-balance.';

// The cohort's score weights: everyone can read them (so a ranking can be
// explained); staff with UpdateCohort can change them.
export default function WeightsPanel({ cohortId, canEdit }) {
  const { data, isLoading, error } = useWeights(cohortId);
  const save = useSaveWeights(cohortId);
  const [draft, setDraft] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    setDraft(data?.weights ? { ...data.weights } : null);
    setConfirmReset(false);
  }, [data]);

  if (isLoading)
    return (
      <div className={`${CARD} h-40 animate-pulse`} aria-label="Loading" />
    );
  if (error) {
    return (
      <div className={`${CARD} p-5 text-sm text-gray-600`}>
        {siError(error, "We couldn't load the score weights.")}
      </div>
    );
  }
  if (!draft) return null;

  const keys = componentKeys(data.defaults ?? data.weights);
  const dirty = keys.some((k) => Number(draft[k]) !== Number(data.weights[k]));
  const invalid = keys.some((k) => {
    const v = Number(draft[k]);
    return draft[k] === '' || Number.isNaN(v) || v < 0 || v > 100;
  });
  const allZero = keys.every((k) => Number(draft[k]) === 0);

  const run = (weights, success) =>
    save.mutate(weights, {
      onSuccess: () => toast.success(success),
      onError: (err) =>
        toast.error(siError(err, "We couldn't save the weights.")),
    });

  return (
    <section className={`${CARD} space-y-4 p-5`}>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-gray-900">
          How scores are calculated
        </p>
        <p className="text-xs text-gray-500">{EXPLAINER}</p>
        <p className="text-xs text-gray-500">
          {data.custom
            ? `This cohort uses its own weights${
                data.updated_by ? `, set by ${data.updated_by}` : ''
              }${data.updated_at ? ` on ${formatDay(data.updated_at)}` : ''}.`
            : 'This cohort uses the default weights.'}
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        {keys.map((key) => (
          <label
            key={key}
            className="flex flex-col gap-1 text-xs text-gray-600"
          >
            {componentLabel(key)}
            {canEdit ? (
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                aria-label={`${componentLabel(key)} weight`}
                value={draft[key] ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    [key]: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                className={`${INPUT} w-24`}
              />
            ) : (
              <span className="text-sm font-medium text-gray-900">
                {data.weights[key]}
              </span>
            )}
            {data.defaults &&
              Number(data.defaults[key]) !== Number(draft[key]) && (
                <span className="text-[10px] text-gray-400">
                  default {data.defaults[key]}
                </span>
              )}
          </label>
        ))}
      </div>
      {canEdit && (invalid || allZero) && (
        <p className="text-xs text-red-700">
          {invalid
            ? 'Each weight must be between 0 and 100.'
            : 'At least one weight must be above 0.'}
        </p>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!dirty || invalid || allZero || save.isLoading}
            onClick={() =>
              run(
                Object.fromEntries(keys.map((k) => [k, Number(draft[k])])),
                'Weights saved. Rankings are recalculated.'
              )
            }
            className="rounded-lg bg-persian-500 px-4 py-1.5 text-sm text-white hover:bg-persian-600 disabled:opacity-50"
          >
            {save.isLoading ? 'Saving…' : 'Save weights'}
          </button>
          {dirty && (
            <button
              type="button"
              onClick={() => setDraft({ ...data.weights })}
              className="rounded-lg border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Discard
            </button>
          )}
          {data.custom && !confirmReset && (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className="text-sm text-persian-600 hover:underline"
            >
              Reset to defaults…
            </button>
          )}
          {data.custom && confirmReset && (
            <span className="flex items-center gap-2 text-sm text-gray-700">
              Use the default weights for this cohort?
              <button
                type="button"
                onClick={() => run(null, 'Default weights restored.')}
                disabled={save.isLoading}
                className="rounded-lg bg-persian-500 px-3 py-1 text-sm text-white hover:bg-persian-600 disabled:opacity-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setConfirmReset(false)}
                className="text-sm text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </span>
          )}
        </div>
      )}
    </section>
  );
}
